import { Component, DestroyRef, OnDestroy, OnInit, ViewChild, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { AlertService } from 'src/app/services/alert.service';
import { MatSort, Sort } from '@angular/material/sort';
import { TableSortService } from 'src/app/services/table-sort.service';
import { TableId } from 'src/app/constants/table.constant';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject, takeUntil, finalize } from 'rxjs';
import { DeleteModalComponent } from 'src/app/components/shared/delete-modal/delete-modal.component';
import { IBatch } from 'src/app/models/batches/batch';
import { IUserView } from 'src/app/models/users/user-result';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { environment } from 'src/environments/environment';
import { UserCreateComponent } from '../user-create/user-create.component';
import { UserUpdateComponent } from '../user-update/user-update.component';
import { AuthService } from 'src/app/services/auth.service';
import { IPreference, IUserProfile } from 'src/app/models/users/userProfile';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRStaticGroups } from 'src/app/constants/signalr.constant';
import { determineUserTypeFromGroups, parseGroupToDisplayName } from 'src/app/models/users/userGroups.helpers';
import { UserType } from 'src/app/models/users/userGroups';
import { Permission } from 'src/app/constants/permission.constant';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class UserListComponent implements OnInit, OnDestroy {
  apiBase = environment.apiBase;
  Permission = Permission;
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();

  // Service Constructor
  constructor(
    private router: Router,
    private usersService: UsersService,
    public dialog: MatDialog,
    private alertService: AlertService,
    public authService: AuthService,
    private signalrService: SignalrService,
    private tableSortService: TableSortService,
  ) {}
  users: IUserView[] = [];
  loading: boolean = true;
  currentConfigDate!: IPreference;
  user!: IUserProfile | null;
  dataSource = new MatTableDataSource<IUserView>();
  filter: any;
  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toUpperCase();
  }
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  @ViewChild(MatSort)
  sort!: MatSort;
  displayedColumns: string[] = [
    'profileImage',
    'name',
    'emailAddress',
    'groups',
    'createdAt',
    'actions',
  ];

  ngOnInit() {
    this.user = this.authService.getAuthInformation();
    this.listUsers();
    this.authService.currentPreference.subscribe((res) => {
      this.currentConfigDate = res;
    });
    SignalrGroup.join({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRStaticGroups.UsersList,
      data$: this.signalrService.usersData,
      onData: (data: any) => {
        if (data.id) {
          const parsedUser = this.parseUser(data);
          const index = this.users.findIndex((p) => p.id == data.id);
          if (index >= 0) {
            this.users[index] = parsedUser;
          } else {
            this.users = [...this.users, parsedUser];
          }
          this.dataSource.data = this.users;
        }
      },
    });
  }

  listUsers() {
    this.users = [];
    this.dataSource = new MatTableDataSource<IUserView>(this.users);
    this.usersService
      .listUsers()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (result) => {
          if (result.users) {
            if (isDevMode()) {
              console.log('[DEV] [UserListComponent] users found: ', result.users);
            }
            this.users = result.users
              .sort((a: IUserView, b: IUserView) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)
              .map((user: IUserView) => this.parseUser(user));
            this.dataSource = new MatTableDataSource<IUserView>(this.users);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.tableSortService.applySortToMatSort(this.sort, TableId.USER_LIST);
          }
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  parseUser(user: IUserView): IUserView & { _userType: string; _isInconsistent: boolean; _groups: string[] } {
    const { userType, isConsistent } = determineUserTypeFromGroups(user.groups || []);
    const isClient = userType === UserType.Client;
    const displayNames = (user.groups || []).map((g: string) => parseGroupToDisplayName(g)).filter(Boolean).join(', ');

    const clientName = user.organizationName ? `Client (${user.organizationName})` : 'Client';
    const unknownName = `Unknown (${displayNames || '<none>'})`;
    const _userType = isClient ? clientName : (userType ?? unknownName);

    const _isInconsistent = !isClient && (userType === null || !isConsistent);

    return { ...user, _userType, _isInconsistent, _groups: [_userType] };
  }

  updateUser(user: IUserView) {
    const dialogRef = this.dialog.open(UserUpdateComponent, {
      data: user,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.listUsers();
      }
    });
  }

  resendInvite(user: IUserView) {
    if (!user.id) return;
    this.loading = true;
    this.usersService
      .resendInvite(user.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.successToast('Invite sent successfully');
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  deleteUser(user: IUserView) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        header: `Delete User: ${user.name}`,
        message: 'Are you sure you want to delete this user, action cannot be un-done?',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.confimDelete(user);
      }
    });
  }

  confimDelete(user: IUserView) {
    this.loading = true;
    this.usersService
      .deleteUser(user)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.successToast('Successfully Deleted');
          this.listUsers();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  createUser() {
    const dialogRef = this.dialog.open(UserCreateComponent);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.listUsers();
      }
    });
  }

  onSortChange(sort: Sort): void {
    this.tableSortService.setSortState(TableId.USER_LIST, sort);
  }

  setPageSizePreference(event: any) {
    if (event.pageSize != this.currentConfigDate.pageSize) {
      this.currentConfigDate.pageSize = event.pageSize;
      let payload = {
        name: this.user?.name,
        preference: this.currentConfigDate,
      };
      this.authService.updatePreference(payload).subscribe((res) => {
        if (isDevMode()) {
          console.log('[DEV] [UserListComponent] res', res);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.signalrService.clearUsers();
  }
}
